import {
  submitSetReviewStatusImportRequest,
  submitDmcaTakedownNoticesImportRequest,
  submitDmcaEscalationImportRequest,
  submitCloudflareAdhocImportRequest,
  submitDmcaRetractionNoticesImportRequest,
  submitResultsImportRequest,
  submitMarkUrlsDownImportRequest,
  submitTakedownStatsImportRequest,
  submitMarkHostsDownImportRequest,
  submitDowncheckUrlsImportRequest,
} from '@Actions';
import { paths } from '@Components/configs';
/* eslint-disable import/no-extraneous-dependencies */
import { AuthorizedWorkReference } from '@RedflagAI/node-utility/interface';
import {
  RESULT_CATEGORY_COLUMNS,
  RESULT_DEFAULT_CATEGORY,
  RESULTS_IMPORT_EXTRA_FIELDS,
} from '@RedflagAI/node-utility/csv';
/* eslint-enable import/no-extraneous-dependencies */

const RESULT_CATEGORY_COLUMN_MAP = Object
  .entries(RESULT_CATEGORY_COLUMNS)
  .reduce((acc, [key, config]) => {
    acc[key] = Array.isArray(config?.columns) ? config.columns : [];
    return acc;
  }, {});

/**
 * Within the admin panel, each import type includes details such as the CSV
 * structure, required fields, and the action to be taken upon submission.
 * New import types can be added by extending this configuration array. Each
 * import configuration object contains:
 * - key: Unique identifier for the import type
 * - label: Display name for the import type
 * - path: URL path for accessing the import page
 * - category: Category of the import type, one of 'compliance', 'results', or 'searches'
 * - csvConfig: Configuration for the CSV file structure
 * - requiredInputKeys: List of required input keys for the import form
 * - extraFields: Additional fields specific to the import type
 * --- extraFields should be an array of objects with:
 * ----- name: Field name
 * ----- label: Field label for display
 * ----- type: Input type (e.g., singleTextInput, radioSelect)
 * ------ more types support can be added as needed in the NewFileImportPage component
 * ----- options: (if a selection type input) Array of option values
 * - submitAction: Function to call when the form is submitted
 */

const fileImportsConfig = [
  {
    key: 'set-review-status',
    label: 'Set Review Status',
    path: paths.adminFileImportNew.replace(':id?', 'set-review-status'),
    category: 'results',
    csvConfig: [
      {
        name: 'url',
        description: 'Primary URL of the infringement to set review status for',
        required: true,
      },
      {
        name: 'internal_review_status',
        description: 'Internal review status for this infringement',
        required: true,
      },
      {
        name: 'review_status',
        description: 'Review status for this infringement',
        required: true,
      },
      {
        name: 'id',
        description: 'Alternative unique identifier for the infringement, used over url if both provided',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: null,
    submitAction: submitSetReviewStatusImportRequest,
  },
  {
    key: 'dmca-takedown-notices',
    label: 'Send DMCA Takedown Notices',
    path: paths.adminFileImportNew.replace(':id?', 'dmca-takedown-notices'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'url',
        description: 'URL of the infringement to send DMCA takedown notice for',
        required: true,
      },
      {
        name: 'notify_website_owner',
        description: 'Whether to notify the website owner (true/false)',
        required: true,
      },
      {
        name: 'notify_hosting_provider',
        description: 'Whether to notify the hosting provider (true/false)',
        required: true,
      },
      {
        name: 'notify_bing',
        description: 'Whether to notify Bing (true/false)',
        required: true,
      },
      {
        name: 'notify_google',
        description: 'Whether to notify Google (true/false)',
        required: true,
      },
      {
        name: 'notify_social_platform',
        description: 'Whether to notify social platform (true/false)',
        required: true,
      },
      {
        name: 'id',
        description: 'Alternative unique identifier, used over url if both provided',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'force_dmca',
        label: 'Force DMCA',
        type: 'checkbox',
        required: false,
      },
    ],
    submitAction: submitDmcaTakedownNoticesImportRequest,
  },
  {
    key: 'dmca-escalation',
    label: 'Escalate DMCA Notices',
    path: paths.adminFileImportNew.replace(':id?', 'dmca-escalation'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'id',
        description: 'Mongo result document ID to escalate',
        required: true,
        sample: '64f2c9c9f1b2d178e4f89310',
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: null,
    submitAction: submitDmcaEscalationImportRequest,
  },
  {
    key: 'results-imports',
    label: 'Import Results',
    path: paths.adminFileImportNew.replace(':id?', 'results-imports'),
    category: 'results',
    csvConfigByCategory: RESULT_CATEGORY_COLUMN_MAP,
    csvDefaultCategory: RESULT_DEFAULT_CATEGORY,
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: RESULTS_IMPORT_EXTRA_FIELDS,
    submitAction: submitResultsImportRequest,
  },
  {
    key: 'mark-urls-down',
    label: 'Mark URLs Down',
    path: paths.adminFileImportNew.replace(':id?', 'mark-urls-down'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'id',
        description: 'Unique identifier for the infringement, used as an additional filter',
        required: false,
      },
      {
        name: 'url',
        description: 'Primary URL of the infringement to update down status for',
        required: true,
      },
      {
        name: 'down_status',
        description: 'Down status for this infringement (up/down)',
        required: true,
      },
      {
        name: 'down_date',
        description: 'Down date for the infringement in YYYY-MM-DD',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: null,
    submitAction: submitMarkUrlsDownImportRequest,
  },
  {
    key: 'set-takedown-dates',
    label: 'Set Takedown Dates',
    path: paths.adminFileImportNew.replace(':id?', 'set-takedown-dates'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'id',
        description: 'Alternative unique identifier, used over url if both provided',
        required: false,
      },
      {
        name: 'url',
        description: 'Primary URL of the infringement to update takedown dates for',
        required: true,
      },
      {
        name: 'takedown_date',
        description: 'Date of the takedown notice in YYYY-MM-DD',
        required: true,
      },
      {
        name: 'google_delisted_date',
        description: 'Date of the Google takedown notice in YYYY-MM-DD',
        required: false,
      },
      {
        name: 'bing_delisted_date',
        description: 'Date of the Bing takedown notice in YYYY-MM-DD',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'replace_existing',
        label: 'Replace existing takedown dates',
        type: 'checkbox',
        required: false,
      },
    ],
    submitAction: submitTakedownStatsImportRequest,
  },
  {
    key: 'mark-hosts-down',
    label: 'Mark Hosts Down',
    path: paths.adminFileImportNew.replace(':id?', 'mark-hosts-down'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'host',
        description: 'Host to mark as down',
        required: true,
      },
      {
        name: 'down_at',
        description: 'Down date for the host in YYYY-MM-DD',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'include_marking_urls_as_down',
        label: 'Include marking urls as down',
        type: 'checkbox',
        required: false,
      },
    ],
    submitAction: submitMarkHostsDownImportRequest,
  },
  {
    key: 'downcheck-urls',
    label: 'Downcheck URLs',
    path: paths.adminFileImportNew.replace(':id?', 'downcheck-urls'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'id',
        description: 'Alternative unique identifier, used over url if both provided',
        required: false,
      },
      {
        name: 'url',
        description: 'URLs to downcheck, comma separated if multiple',
        required: true,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'send_next_round_dmca',
        label: 'Send next round of DMCA notices if URL is not down',
        description: 'Selecting this will schedule the URL for the next round of DMCA checks. The URL will be checked up to three times — once immediately, then again after 3 days, and once more after another 3 days. If the content remains active after all checks, a new DMCA notice will be sent.',
        type: 'checkbox',
        required: false,
      },
    ],
    submitAction: submitDowncheckUrlsImportRequest,
  },
  {
    key: 'cloudflare-adhoc-request',
    label: 'Send Cloudflare Adhoc Request',
    path: paths.adminFileImportNew.replace(':id?', 'cloudflare-adhoc-request'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'url',
        description: 'URL of the infringement to send Cloudflare notices for',
        required: true,
      },
      {
        name: 'domain_name',
        description: 'Domain name of the website for the result',
        required: true,
      },
      {
        name: 'title',
        description: 'Title of the content to send notices for',
        required: true,
      },
      {
        name: 'authorized_work_id',
        description: 'ID of the authorized work to send notices for (required for IMDB requests)',
        required: true,
      },
      {
        name: 'original_link',
        description: 'Link to the original authorized work (required for non-IMDB requests)',
        required: true,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'authorized_work_reference',
        label: 'Authorized Work Reference',
        type: 'radioSelect',
        options: Object.values(AuthorizedWorkReference),
        required: true,
      },
    ],
    submitAction: submitCloudflareAdhocImportRequest,
  },
  {
    key: 'dmca-retraction-notices',
    label: 'Send DMCA Retraction Notice',
    path: paths.adminFileImportNew.replace(':id', 'dmca-retraction-notices'),
    category: 'compliance',
    csvConfig: [
      {
        name: 'url',
        description: 'URL of the infringement to send DMCA retraction notice for',
        required: true,
      },
      {
        name: 'id',
        description: 'Alternative unique identifier, used over url if both provided',
        required: false,
      },
    ],
    requiredInputKeys: ['name', 'company_id', 'file'],
    extraFields: [
      {
        key: 'recipient_email',
        label: 'Recipient Email',
        type: 'singleTextInput',
        required: true,
      },
      {
        key: 'subject',
        label: 'Subject',
        type: 'singleTextInput',
        required: true,
      },
      {
        key: 'force_notices_on_not_found_urls',
        label: 'Force Notices on Not Found URLs',
        type: 'checkbox',
        required: false,
      },
    ],
    submitAction: submitDmcaRetractionNoticesImportRequest,
  },
  // Add more import configs here
];

export default fileImportsConfig;
